import { makeAutoObservable, observable, ObservableMap, values } from 'mobx'
import { defaultMobxCacheMaxAge, type RootStore } from 'stores/RootStore'
import { Notification, AccessRequestNotification } from 'blaze-components/NotificationsTray/NotificationItem/types'
import { NotificationsClient } from 'api/clients/NotificationsClient'
import { Permission, Workspace } from 'types'
import * as AppPreferencesClient from 'api/clients/AppPreferencesClient'
import { SUCCESS_RESPONSE_TYPE } from 'api/constants'
import { showToastMessage } from 'common/ToastMessage/ShowToastMessage'
import dayjs from 'dayjs'
import { makePersistable } from 'mobx-persist-store'

export enum ViewTypes {
  Read = 'read',
  Unread = 'unread',
}

type ViewType = ViewTypes.Read | ViewTypes.Unread

export class NotificationsStore {
  rootStore: RootStore
  currentView: ViewType | null
  mutedAt: string | null
  notifications: ObservableMap<number, Notification> = observable.map()

  constructor(rootStore: RootStore, shouldPersist = true) {
    this.rootStore = rootStore
    this.currentView = ViewTypes.Unread
    this.mutedAt = null
    makeAutoObservable(this)

    if (shouldPersist) {
      makePersistable(this, {
        name: 'NotificationsStore',
        properties: ['notifications'],
        storage: window.localStorage,
        stringify: false,
        expireIn: defaultMobxCacheMaxAge,
      })
    }
  }

  clear = () => {
    this.notifications.clear()
  }

  setCurrentView = (view: ViewType | null) => {
    this.currentView = view
  }

  add = (notification: Notification) => {
    this.notifications.set(notification.id, notification)
  }

  bulkAdd = (notifications: Notification[]) => {
    notifications.forEach(this.add)
  }

  get = (id: number) => {
    return this.notifications.get(id)
  }

  get unread() {
    return values(this.notifications)
      .filter((notification) => !notification.read && !!notification.subject)
      .sort((a, b) => (dayjs(a.updated_at).isBefore(dayjs(b.updated_at)) ? 1 : -1))
  }

  get read() {
    return values(this.notifications)
      .filter((notification) => notification.read && !!notification.subject)
      .sort((a, b) => (dayjs(a.updated_at).isBefore(dayjs(b.updated_at)) ? 1 : -1))
  }

  get unseen() {
    return this.unread.filter((notification) => !notification.seen)
  }

  // Marks all 'unseen' notifications as 'seen'
  markAllAsSeen = async (currentWorkspace: Workspace) => {
    const unseenIds = this.unseen.map((n) => n.id)
    this.unseen.forEach((notification) => (notification.seen = true))

    const response = await NotificationsClient.markAllAsSeen(currentWorkspace.id)
    if (response.type !== SUCCESS_RESPONSE_TYPE) {
      // revert if error
      unseenIds.forEach((unseenId) => {
        const notification = this.notifications.get(unseenId)
        if (notification) notification.seen = false
      })

      throw new Error(response.data.message)
    } else {
      // Keep unseen notifications count on top of bell up to date
      this.rootStore.queryClient.invalidateQueries(['useNotifications', false, false, currentWorkspace.key])
    }
  }

  // Marks all 'unread' notifications as 'read'
  markAllAsRead = async (currentWorkspace: Workspace) => {
    const unreadIds = this.unread.map((n) => n.id)
    this.unread.forEach((notification) => (notification.read = true))

    const response = await NotificationsClient.markAllAsRead(currentWorkspace.id)
    if (response.type !== SUCCESS_RESPONSE_TYPE) {
      // revert if error
      unreadIds.forEach((unreadId) => {
        const notification = this.notifications.get(unreadId)
        if (notification) notification.read = false
      })

      throw new Error(response.data.message)
    } else {
      // Keep unseen notifications count on top of bell up to date
      this.rootStore.queryClient.invalidateQueries(['useNotifications', false, false, currentWorkspace.key])
    }
  }

  // Marks the given notification as 'read'
  markAsRead = async (notificationId: number, currentWorkspaceKey: string) => {
    const notification = this.notifications.get(notificationId)
    if (!notification) return

    notification.read = true
    const response = await NotificationsClient.markAsRead(notificationId)
    if (response.type !== SUCCESS_RESPONSE_TYPE) {
      // revert if error
      notification.read = false

      throw new Error(response.data.message)
    } else {
      // Keep unseen notifications count on top of bell up to date
      this.rootStore.queryClient.invalidateQueries(['useNotifications', false, false, currentWorkspaceKey])
    }
  }

  // Marks the given access request notification as 'granted'
  markAccessAsGranted = async (notificationId: number, permission?: Permission) => {
    const notification = this.notifications.get(notificationId) as AccessRequestNotification
    const subject = notification.subject
    const grantedPermission = permission || subject.requested_permission
    if (!notification || !grantedPermission) return

    subject.permission_granted = true
    notification.read = true

    const response = await NotificationsClient.acceptAccessRequest(subject.token, grantedPermission, false)
    if (response.type === SUCCESS_RESPONSE_TYPE) {
      showToastMessage('Access granted!', 'success')
    } else {
      subject.permission_granted = false
      notification.read = false
      showToastMessage('An error occurred, please try again.', 'error')
    }
  }

  toggleMute = async () => {
    this.mutedAt ? this.unmute() : this.mute()
  }

  // Enables Do Not Disturb mode
  mute = async () => {
    const now = dayjs().toISOString()
    this.setMutedAt(now)

    const response = await AppPreferencesClient.updateUserAppPreferences({ notificationsMutedAt: now })
    if (response.type !== SUCCESS_RESPONSE_TYPE) {
      showToastMessage('There was a problem enabling Do Not Disturb, our team has been notified.', 'error')
      this.setMutedAt(null)
    } else {
      showToastMessage('Do Not Disturb enabled', 'success')
    }
  }

  // Disables Do Not Disturb mode
  unmute = async () => {
    const mutedAt = this.mutedAt
    this.setMutedAt(null)

    const response = await AppPreferencesClient.updateUserAppPreferences({ notificationsMutedAt: null })
    if (response.type !== SUCCESS_RESPONSE_TYPE) {
      showToastMessage('There was a problem disabling Do Not Disturb, our team has been notified.', 'error')
      this.setMutedAt(mutedAt)
    } else {
      showToastMessage('Do Not Disturb disabled', 'success')
    }
  }

  setMutedAt = (mutedAt: string | null) => {
    this.mutedAt = mutedAt
  }
}
